import React, { useEffect, useRef, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, Badge } from "@/components/ui-hud";
import { CheckCircle2, Loader2, XCircle, ArrowRight, Radio, IdCard } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { peekPendingRfq, replayPendingRfq } from "@/lib/pendingRfq";

const MAX_POLLS = 45; // 45 × 2s ≈ 90s (Stripe webhooks can lag in preview envs)
const BACKGROUND_POLL_INTERVAL_MS = 5000;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const session_id = params.get("session_id");
  const { refresh } = useAuth();
  const nav = useNavigate();
  const [state, setState] = useState({ phase: "polling", status: null, tier: null, replayedRfq: null });
  const attempts = useRef(0);
  // Keep the latest `refresh` in a ref so the polling effect never re-runs
  // just because AuthContext re-rendered after we called setUser().
  const refreshRef = useRef(refresh);
  useEffect(() => { refreshRef.current = refresh; }, [refresh]);

  useEffect(() => {
    if (!session_id) {
      setState({ phase: "error", status: "missing_session" });
      return;
    }
    // Fresh session — reset the attempt counter so a previous /payment/success
    // visit doesn't carry over and instantly jump us to the timeout screen.
    attempts.current = 0;
    let mounted = true;
    let bgTimer = null;

    const tryResolvePaid = async () => {
      const r = await api.get(`/payments/status/${session_id}`);
      if (r.data.payment_status === "paid") {
        try { await refreshRef.current(); } catch { /* auth refresh is best-effort */ }
        let replayedRfq = null;
        if (peekPendingRfq()) {
          try { replayedRfq = await replayPendingRfq(); } catch { /* non-blocking */ }
        }
        if (mounted) {
          const addons = r.data.addon_lookup_keys || [];
          setState({
            phase: "paid",
            status: r.data,
            tier: r.data.tier_slug,
            hasDigitalId: addons.includes("pjp_digital_id_once"),
            replayedRfq,
          });
        }
        return true;
      }
      if (r.data.payment_status === "failed" || r.data.payment_status === "expired") {
        if (mounted) setState({ phase: "failed", status: r.data });
        return true;
      }
      return false;
    };

    const tick = async () => {
      if (!mounted) return;
      attempts.current += 1;
      try {
        const done = await tryResolvePaid();
        if (done) return;
      } catch {
        // Transient — keep polling
      }
      if (!mounted) return;
      if (attempts.current >= MAX_POLLS) {
        // Show the "still processing" screen but keep polling silently in the
        // background — most Stripe webhooks land within 30-60s and the user
        // doesn't need to refresh anything.
        setState({ phase: "timeout" });
        const bgTick = async () => {
          if (!mounted) return;
          try {
            const done = await tryResolvePaid();
            if (done) return;
          } catch { /* keep trying */ }
          if (mounted) bgTimer = setTimeout(bgTick, BACKGROUND_POLL_INTERVAL_MS);
        };
        bgTimer = setTimeout(bgTick, BACKGROUND_POLL_INTERVAL_MS);
        return;
      }
      setTimeout(tick, 2000);
    };
    tick();
    return () => {
      mounted = false;
      if (bgTimer) clearTimeout(bgTimer);
    };
  }, [session_id]);

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-6 py-16">
      <Card className="max-w-lg w-full text-center">
        {state.phase === "polling" && (
          <>
            <Loader2 className="h-10 w-10 text-cyan mx-auto animate-spin mb-4" />
            <div className="overline text-cyan mb-2">CONFIRMING PAYMENT</div>
            <h1 className="font-display text-2xl font-bold tracking-tight mb-2">Checking with Stripe…</h1>
            <p className="text-sm text-slate-400">Your card was charged. We're activating your membership.</p>
          </>
        )}
        {state.phase === "paid" && (
          <>
            <CheckCircle2 className="h-12 w-12 text-emerald-400 mx-auto mb-4" />
            <div className="overline text-emerald-400 mb-2">MEMBERSHIP ACTIVE</div>
            <h1 className="font-display text-3xl font-bold tracking-tight mb-2">You're in.</h1>
            {/* Digital ID bundled banner — renders on every tier variant when the addon was included */}
            {state.hasDigitalId && state.tier !== "digital_id_addon" && (
              <div className="mt-3 mx-auto max-w-sm rounded-lg border border-orange-hud/40 bg-orange-hud/[0.08] p-3 flex items-center gap-3 text-left" data-testid="success-digital-id-bundled">
                <IdCard className="h-5 w-5 text-orange-hud flex-shrink-0" />
                <div className="flex-1">
                  <div className="mono text-[10px] tracking-[0.24em] uppercase text-orange-hud">// Digital ID · queued to ship</div>
                  <div className="text-[13px] text-white/90 leading-snug">
                    Your PJP Digital ID is being FAA-verified now. Ships in 24-48h.
                  </div>
                </div>
              </div>
            )}
            {state.replayedRfq ? (
              <>
                <p className="text-sm text-slate-300 mb-2">
                  Your Charter Client membership is live — <b className="text-cyan">and your trip is already working</b>.
                </p>
                <div className="mt-3 mx-auto max-w-sm rounded-lg border border-cyan/40 bg-cyan/[0.06] p-3 text-left" data-testid="success-rfq-restored">
                  <div className="mono text-[10px] tracking-[0.24em] uppercase text-cyan/80 mb-1 flex items-center gap-1.5">
                    <Radio className="h-3 w-3 animate-pulse" /> // Trip preserved · dispatched to operators
                  </div>
                  <div className="text-sm text-white/90">
                    We picked up right where you left off. Operators are being notified now — head to your dashboard to watch bids land.
                  </div>
                </div>
                <div className="mt-6 flex gap-3 justify-center">
                  <Link to="/charter-dashboard" data-testid="btn-success-charter-dash" className="btn-hud btn-hud-solid">OPEN CHARTER DASHBOARD <ArrowRight className="h-3.5 w-3.5" /></Link>
                </div>
              </>
            ) : state.tier === "empty_leg_rider" ? (
              <>
                <p className="text-sm text-slate-300 mb-2">
                  Welcome aboard, PJP <b className="text-cyan">JumpSeat Rider</b>. Your $10K annual membership is live.
                </p>
                <p className="text-xs text-white/60 mb-4">
                  Next: complete your Secure Flight (TSA) profile and we'll start pushing matching JumpSeats your way.
                </p>
                <div className="mt-6 flex gap-3 justify-center flex-wrap">
                  <Link to="/rider" data-testid="btn-success-rider-dash" className="btn-hud btn-hud-solid">OPEN RIDER DASHBOARD <ArrowRight className="h-3.5 w-3.5" /></Link>
                  <Link to="/rider/tsa" data-testid="btn-success-rider-tsa" className="btn-hud">Complete TSA profile</Link>
                </div>
              </>
            ) : (
              <>
                <p className="text-sm text-slate-400 mb-2">
                  {state.tier === "digital_id_addon"
                    ? "Your Digital ID is on the way. You'll get an email when it ships."
                    : "Welcome to PJP. Your membership is now active."}
                </p>
                <div className="mt-6 flex gap-3 justify-center">
                  <Link to="/dashboard" data-testid="btn-success-dashboard" className="btn-hud btn-hud-solid">GO TO COCKPIT <ArrowRight className="h-3.5 w-3.5" /></Link>
                  <Link to="/profile" data-testid="btn-success-profile" className="btn-hud">EDIT PROFILE</Link>
                </div>
              </>
            )}
          </>
        )}
        {state.phase === "failed" && (
          <>
            <XCircle className="h-12 w-12 text-red-400 mx-auto mb-4" />
            <div className="overline text-red-400 mb-2">PAYMENT ISSUE</div>
            <h1 className="font-display text-2xl font-bold tracking-tight mb-2">We couldn't complete that charge.</h1>
            <Link to="/membership" data-testid="btn-success-retry" className="btn-hud btn-hud-solid mt-4">TRY AGAIN</Link>
          </>
        )}
        {state.phase === "timeout" && (
          <>
            <Loader2 className="h-10 w-10 text-cyan mx-auto mb-4 animate-spin" />
            <div className="overline text-cyan mb-2">STILL PROCESSING</div>
            <h1 className="font-display text-2xl font-bold tracking-tight mb-2">Your card is safe — we're still confirming.</h1>
            <p className="text-sm text-slate-400">
              Stripe webhooks sometimes take up to a minute in test mode. We'll keep checking automatically — no need to refresh.
            </p>
            <div className="mt-6 flex flex-wrap gap-3 justify-center items-center">
              <button
                type="button"
                onClick={() => window.location.reload()}
                data-testid="btn-success-refresh"
                className="btn-hud btn-hud-solid"
              >
                REFRESH NOW
              </button>
              <Link to="/dashboard" data-testid="btn-success-cockpit-fallback" className="btn-hud">GO TO COCKPIT</Link>
            </div>
            <div className="mt-4"><Badge tone="mono">SESSION · {session_id?.slice(-8)}</Badge></div>
          </>
        )}
        {state.phase === "error" && (
          <>
            <XCircle className="h-12 w-12 text-red-400 mx-auto mb-4" />
            <h1 className="font-display text-2xl font-bold tracking-tight">Missing session reference.</h1>
            <Link to="/membership" className="btn-hud mt-4 inline-flex">BACK TO MEMBERSHIP</Link>
          </>
        )}
      </Card>
    </div>
  );
}
